import React, { useState, useEffect } from 'react';
import {
    FaUsers, FaEye, FaSearch, FaFilter, FaFilePdf, FaCheck, FaTimes,
    FaCalendarAlt, FaEnvelope, FaPhoneAlt, FaUserTie, FaTrash, FaCheckCircle,
    FaUserPlus, FaExternalLinkAlt, FaQuoteLeft, FaHardHat, FaUserShield, FaBriefcase, FaUserCircle
} from 'react-icons/fa';
import pb from '../../lib/pocketbase';
import styles from '../../pages/Account.module.css';
import { toast } from 'react-toastify';

const TabRecruitment = ({ user }) => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [selectedApp, setSelectedApp] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [updating, setUpdating] = useState(false);

    // Hiring Modal States
    const [isHireModalOpen, setIsHireModalOpen] = useState(false);
    const [candidateToHire, setCandidateToHire] = useState(null);
    const [selectedRole, setSelectedRole] = useState('employee');
    const [hiringProcessing, setHiringProcessing] = useState(false);

    const statuses = [
        { id: 'NOUVEAU', label: 'Nouveau', color: '#3b82f6', bg: '#eff6ff' },
        { id: 'EN_COURS', label: 'En examen', color: '#f59e0b', bg: '#fffbeb' },
        { id: 'ENTRETIEN', label: 'Entretien', color: '#8b5cf6', bg: '#f5f3ff' },
        { id: 'ACCEPTE', label: 'Accepté', color: '#10b981', bg: '#ecfdf5' },
        { id: 'REFUSE', label: 'Refusé', color: '#ef4444', bg: '#fef2f2' }
    ];

    const fetchApplications = async () => {
        setLoading(true);
        try {
            // Fetch users who are strictly categorized as candidates
            const records = await pb.collection('users').getFullList({
                filter: 'profile_type = "candidate"',
                sort: '-created',
            });
            setApplications(records);
        } catch (error) {
            console.error('Error fetching candidates:', error);
            toast.error("Erreur lors de la récupération des candidatures.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchApplications();
        pb.collection('users').subscribe('*', (e) => {
            if (e.action === 'update' || e.action === 'create' || e.action === 'delete') {
                fetchApplications();
            }
        }).catch(err => {
            if (err.status !== 0) console.error(err);
        });
        return () => pb.collection('users').unsubscribe('*').catch(() => { });
    }, []);

    const handleUpdateStatus = async (appId, newStatus) => {
        setUpdating(true);
        try {
            await pb.collection('users').update(appId, { application_status: newStatus });
            toast.success("Dossier mis à jour !");
            if (selectedApp && selectedApp.id === appId) {
                setSelectedApp({ ...selectedApp, application_status: newStatus });
            }
            fetchApplications();
        } catch (error) {
            toast.error("Erreur lors de la mise à jour.");
        } finally {
            setUpdating(false);
        }
    };

    const handleDelete = async (appId) => {
        if (!window.confirm("Supprimer cette candidature définitivement ?")) return;
        try {
            await pb.collection('users').delete(appId);
            toast.success("Candidature supprimée.");
            setShowDetailModal(false);
            fetchApplications();
        } catch (error) {
            toast.error("Erreur lors de la suppression.");
        }
    };

    const handleHireClick = (app) => {
        setCandidateToHire(app);
        setSelectedRole('employee');
        setIsHireModalOpen(true);
        setShowDetailModal(false);
    };

    const finalizeHire = async () => {
        if (!candidateToHire) return;

        try {
            setHiringProcessing(true);
            const userToUpdate = candidateToHire;

            // Determine profile type based on role
            let profileType = 'staff';
            if (selectedRole === 'client') profileType = 'client';
            if (selectedRole === 'user') profileType = 'candidate';

            const updatePayload = {
                role: selectedRole,
                profile_type: profileType,
                application_status: 'ACCEPTE',
                emailVisibility: true
            };

            await pb.collection('users').update(userToUpdate.id, updatePayload);
            toast.success(`Succès ! ${userToUpdate.name} fait maintenant partie de l'équipe.`);

            setIsHireModalOpen(false);
            fetchApplications();

        } catch (error) {
            console.error("Hiring failed:", error);
            toast.error("Échec de l'embauche : " + (error.message || "Erreur inconnue"));
        } finally {
            setHiringProcessing(false);
        }
    };

    const filteredApps = applications.filter(app => {
        const name = (app.name || '').toLowerCase();
        const email = (app.email || '').toLowerCase();
        const position = (app.position || '').toLowerCase();
        const query = searchTerm.toLowerCase();

        const matchesSearch = name.includes(query) || email.includes(query) || position.includes(query);
        const matchesStatus = statusFilter === 'ALL' || app.application_status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const openDetails = (app) => {
        setSelectedApp(app);
        setShowDetailModal(true);
    };

    return (
        <div className={styles.tabContent}>
            <div className={styles.inventoryHeader}>
                <div>
                    <h2 className={styles.contentTitle} style={{ margin: 0 }}>Gestion du Recrutement</h2>
                    <p style={{ margin: '5px 0 0 0', color: '#64748b', fontSize: '0.9rem' }}>Interface d'administration des candidatures et nouveaux talents.</p>
                </div>
                <div className={styles.kpiCard} style={{ padding: '8px 15px', minWidth: 'auto', background: 'var(--color-primary)', color: 'white' }}>
                    <div style={{ fontSize: '0.7rem' }}>Candidats actifs</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: '800' }}>{applications.length}</div>
                </div>
            </div>

            {/* Filter Bar */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', margin: '25px 0', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: '250px' }}>
                    <FaSearch style={{ position: 'absolute', left: '15px', top: '15px', color: '#94a3b8' }} />
                    <input
                        className={styles.modalSelect}
                        placeholder="Chercher par nom, email ou poste..."
                        style={{ paddingLeft: '45px', width: '100%', borderRadius: '12px' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div style={{ display: 'flex', gap: '5px', overflowX: 'auto', paddingBottom: '2px' }}>
                    <button
                        onClick={() => setStatusFilter('ALL')}
                        className={`${styles.filterBtn} ${statusFilter === 'ALL' ? styles.activeFilter : ''}`}
                    >Tout</button>
                    {statuses.map(s => (
                        <button
                            key={s.id}
                            onClick={() => setStatusFilter(s.id)}
                            className={`${styles.filterBtn} ${statusFilter === s.id ? styles.activeFilter : ''}`}
                            style={{
                                borderColor: statusFilter === s.id ? s.color : '#e2e8f0',
                                color: statusFilter === s.id ? 'white' : '#64748b',
                                background: statusFilter === s.id ? s.color : 'white'
                            }}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className={styles.loadingInner}>Chargement des dossiers...</div>
            ) : (
                <div className={styles.card} style={{ padding: '0' }}>
                    <div className={styles.tableResponsive}>
                        <table className={styles.appTable}>
                            <thead>
                                <tr>
                                    <th>Candidat</th>
                                    <th>Poste / Contact</th>
                                    <th>Date Réception</th>
                                    <th>Statut</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredApps.map(app => (
                                    <tr key={app.id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div className={styles.memberAvatar} style={{ background: '#f1f5f9', color: 'var(--color-primary)' }}>
                                                    {(app.name || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: '800', color: '#1e293b' }}>{app.name || 'Anonyme'}</div>
                                                    <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{app.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: '700', color: '#475569' }}>
                                                {app.position || "Candidature Spontanée"}
                                            </div>
                                            {app.phone && <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{app.phone}</div>}
                                        </td>
                                        <td>
                                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                                <FaCalendarAlt style={{ marginRight: '5px' }} />
                                                {new Date(app.created).toLocaleDateString('fr-FR')}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={styles.typeBadge} style={{
                                                background: statuses.find(s => s.id === app.application_status)?.bg || '#f1f5f9',
                                                color: statuses.find(s => s.id === app.application_status)?.color || '#475569',
                                                fontWeight: 'bold'
                                            }}>
                                                {statuses.find(s => s.id === app.application_status)?.label || app.application_status || 'NOUVEAU'}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    className={styles.miniActionBtn}
                                                    onClick={() => openDetails(app)}
                                                    title="Voir le dossier complet"
                                                >
                                                    <FaEye />
                                                </button>
                                                {app.cv && (
                                                    <a
                                                        href={pb.files.getURL(app, app.cv)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={styles.miniActionBtn}
                                                        style={{ color: '#ef4444' }}
                                                        title="Ouvrir le CV"
                                                    >
                                                        <FaFilePdf />
                                                    </a>
                                                )}
                                                <button
                                                    className={styles.miniActionBtn}
                                                    style={{ color: '#10b981' }}
                                                    onClick={() => handleHireClick(app)}
                                                    title="Embaucher"
                                                    disabled={app.application_status === 'ACCEPTE'}
                                                >
                                                    <FaUserPlus />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredApps.length === 0 && (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                                            <FaUsers size={40} style={{ marginBottom: '10px', opacity: 0.2 }} /><br />
                                            Aucune candidature trouvée.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Application Detail Modal */}
            {showDetailModal && selectedApp && (
                <div className={styles.modalOverlay} onClick={() => setShowDetailModal(false)}>
                    <div className={styles.modalCard} onClick={e => e.stopPropagation()} style={{ maxWidth: '650px' }}>
                        <div className={styles.modalHeader}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <div className={styles.memberAvatar} style={{ width: '40px', height: '40px', fontSize: '1.2rem' }}>
                                    {(selectedApp.name || '?').charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <h3 style={{ margin: 0 }}>Candidature de {selectedApp.name}</h3>
                                    <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Reçu le {new Date(selectedApp.created).toLocaleDateString('fr-FR')}</span>
                                </div>
                            </div>
                            <button className={styles.closeBtn} onClick={() => setShowDetailModal(false)}>×</button>
                        </div>
                        <div className={styles.modalBody} style={{ padding: '25px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                                <div className={styles.infoRow}>
                                    <FaEnvelope style={{ color: 'var(--color-primary)' }} />
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase' }}>Email</label>
                                        <strong>{selectedApp.email}</strong>
                                    </div>
                                </div>
                                <div className={styles.infoRow}>
                                    <FaPhoneAlt style={{ color: '#10b981' }} />
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase' }}>Téléphone</label>
                                        <strong>{selectedApp.phone || "Non renseigné"}</strong>
                                    </div>
                                </div>
                                <div className={styles.infoRow}>
                                    <FaUserTie style={{ color: '#8b5cf6' }} />
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase' }}>Poste visé</label>
                                        <strong>{selectedApp.position || "Candidature Spontanée"}</strong>
                                    </div>
                                </div>
                                <div className={styles.infoRow}>
                                    <FaBriefcase style={{ color: '#f59e0b' }} />
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase' }}>Type Profil</label>
                                        <strong>{selectedApp.profile_type}</strong>
                                    </div>
                                </div>
                            </div>

                            <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '30px', borderLeft: '4px solid var(--color-primary)' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', fontWeight: '800', marginBottom: '10px', color: '#475569' }}>
                                    <FaQuoteLeft style={{ opacity: 0.3 }} /> Message de Motivation
                                </label>
                                <p style={{ margin: 0, fontSize: '1rem', color: '#1e293b', lineHeight: '1.6' }}>
                                    {selectedApp.application_message || "Aucun message d'accompagnement n'a été fourni."}
                                </p>
                            </div>

                            <div style={{ display: 'flex', gap: '15px', marginBottom: '30px' }}>
                                {selectedApp.cv && (
                                    <a
                                        href={pb.files.getURL(selectedApp, selectedApp.cv)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.submitButton}
                                        style={{ background: '#ef4444', flex: 1 }}
                                    >
                                        <FaFilePdf style={{ marginRight: '8px' }} /> Consulter le CV
                                    </a>
                                )}
                                <button
                                    className={styles.submitButton}
                                    onClick={() => handleHireClick(selectedApp)}
                                    style={{ background: '#10b981', flex: 1 }}
                                    disabled={selectedApp.application_status === 'ACCEPTE'}
                                >
                                    <FaUserPlus style={{ marginRight: '8px' }} /> Embaucher
                                </button>
                                <button
                                    className={styles.exportBtn}
                                    onClick={() => handleDelete(selectedApp.id)}
                                    style={{ color: '#ef4444', borderColor: '#ef4444' }}
                                >
                                    <FaTrash />
                                </button>
                            </div>

                            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '25px' }}>
                                <label style={{ display: 'block', fontWeight: '800', marginBottom: '15px' }}>Mettre à jour le statut du dossier</label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
                                    {statuses.map(s => (
                                        <button
                                            key={s.id}
                                            onClick={() => handleUpdateStatus(selectedApp.id, s.id)}
                                            style={{
                                                background: selectedApp.application_status === s.id ? s.color : 'white',
                                                color: selectedApp.application_status === s.id ? 'white' : '#64748b',
                                                border: `1px solid ${selectedApp.application_status === s.id ? s.color : '#e2e8f0'}`,
                                                padding: '10px',
                                                borderRadius: '10px',
                                                fontSize: '0.85rem',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                transition: 'all 0.2s'
                                            }}
                                            disabled={updating}
                                        >
                                            {selectedApp.application_status === s.id && <FaCheckCircle />}
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Hiring Confirmation Modal */}
            {isHireModalOpen && candidateToHire && (
                <div className={styles.modalOverlay} onClick={() => setIsHireModalOpen(false)}>
                    <div className={styles.modalCard} onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div className={styles.modalHeader}>
                            <h3 style={{ margin: 0 }}>Finaliser l'embauche</h3>
                            <button className={styles.closeBtn} onClick={() => setIsHireModalOpen(false)}>×</button>
                        </div>
                        <div className={styles.modalBody} style={{ padding: '25px' }}>
                            <p style={{ margin: '0 0 20px 0', color: '#64748b' }}>
                                Vous allez intégrer <strong>{candidateToHire.name}</strong> à l'équipe. Quel rôle aura ce nouveau collaborateur ?
                            </p>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '25px' }}>
                                {[
                                    { id: 'employee', label: 'Employé', icon: <FaHardHat />, desc: 'Bureau / Terrain' },
                                    { id: 'admin', label: 'Admin', icon: <FaUserShield />, desc: 'Gestion totale' },
                                    { id: 'client', label: 'Client', icon: <FaBriefcase />, desc: 'Partenaire' },
                                    { id: 'user', label: 'Utilisateur', icon: <FaUserCircle />, desc: 'Accès limité' }
                                ].map(role => (
                                    <div
                                        key={role.id}
                                        className={styles.kpiCard}
                                        onClick={() => setSelectedRole(role.id)}
                                        style={{
                                            border: `2px solid ${selectedRole === role.id ? 'var(--color-primary)' : '#e2e8f0'}`,
                                            background: selectedRole === role.id ? 'var(--color-primary-light)' : 'white',
                                            cursor: 'pointer',
                                            padding: '15px',
                                            minWidth: 'auto',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            textAlign: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        <div style={{ fontSize: '1.5rem', color: selectedRole === role.id ? 'var(--color-primary)' : '#94a3b8' }}>
                                            {role.icon}
                                        </div>
                                        <div style={{ fontWeight: '800', fontSize: '0.9rem' }}>{role.label}</div>
                                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{role.desc}</div>
                                    </div>
                                ))}
                            </div>

                            <button
                                className={styles.submitButton}
                                onClick={finalizeHire}
                                disabled={hiringProcessing}
                                style={{ width: '100%', padding: '15px', background: '#10b981' }}
                            >
                                {hiringProcessing ? "Mise à jour du profil..." : "Confirmer l'embauche & Activer l'accès"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TabRecruitment;
